import { useState, useEffect, useRef } from 'react';
import logoIcon from '../assets/icons/MeinGruenraum_Logo.png';
import useAuth from '../hooks/useAuth';
import { AGB_URL, WIDERRUF_URL, DATENSCHUTZ_URL } from '../constants/legal';

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  return isOnline;
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(30);
  const countdownRef = useRef(null);
  const isOnline = useOnlineStatus();
  const { requestMagicLink } = useAuth();

  useEffect(() => {
    if (!sent) return;
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [sent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await requestMagicLink(email, agbAccepted);
      setResendCountdown(30);
      setSent(true);
    } catch (err) {
      if (!isOnline || err instanceof TypeError) {
        setError('Keine Verbindung. Bitte erneut versuchen.');
      } else if (err.status === 429) {
        setError(err.message);
      } else {
        setError('Etwas ist schiefgelaufen. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoIcon} alt="Mein Grünraum Logo" className="h-9 w-9 object-contain" />
          <span className="text-lg font-semibold text-gray-900">Mein Grünraum</span>
        </div>

        {sent ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-3">Link gesendet</h1>
            <p className="text-sm text-gray-600 mb-4">
              Falls ein Account mit dieser E-Mail existiert, haben wir dir einen Login-Link
              gesendet. Prüfe dein Postfach.
            </p>
            <div className="flex flex-col items-center gap-2">
              {resendCountdown > 0 ? (
                <span className="text-sm text-gray-400">
                  Erneut senden in {resendCountdown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    clearInterval(countdownRef.current);
                    setSent(false);
                  }}
                  className="text-sm text-emerald-600 hover:underline"
                >
                  Erneut senden
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  clearInterval(countdownRef.current);
                  setSent(false);
                  setEmail('');
                }}
                className="text-sm text-gray-500 hover:underline"
              >
                Andere E-Mail verwenden
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            {!isOnline && (
              <div className="mb-4 px-3 py-2 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-900">
                  Keine Verbindung. Der Link kann nicht versendet werden.
                </p>
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-900 mb-1">Anmelden</h1>
            <p className="text-sm text-gray-500 mb-5">
              Gib deine E-Mail ein – wir schicken dir einen Login-Link.
            </p>
            <form onSubmit={handleSubmit}>
              <label className="block text-sm text-gray-700 mb-1" htmlFor="email">
                E-Mail-Adresse
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="deine@email.de"
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus -- LoginPage's single primary input; user lands here to type email
                autoFocus
              />
              {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
              <label
                htmlFor="agb-accept"
                className="mt-4 flex items-start gap-2 text-xs text-gray-600 cursor-pointer"
              >
                <input
                  id="agb-accept"
                  type="checkbox"
                  checked={agbAccepted}
                  onChange={(e) => setAgbAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  required
                />
                <span>
                  Ich akzeptiere die{' '}
                  <a
                    href={AGB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline"
                  >
                    AGB
                  </a>{' '}
                  und habe die{' '}
                  <a
                    href={WIDERRUF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline"
                  >
                    Widerrufsbelehrung
                  </a>{' '}
                  sowie die{' '}
                  <a
                    href={DATENSCHUTZ_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline"
                  >
                    Datenschutzerklärung
                  </a>{' '}
                  zur Kenntnis genommen.
                </span>
              </label>
              <button
                type="submit"
                disabled={loading || !isOnline || !agbAccepted}
                className="mt-4 w-full bg-emerald-600 text-white text-sm py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Wird gesendet…' : 'Login-Link senden'}
              </button>
            </form>
          </div>
        )}

        <p className="mt-4 text-xs text-center text-gray-500">
          Du erhältst einen einmaligen Link per E-Mail — kein Passwort nötig.
        </p>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline transition-colors"
          >
            ← Zurück
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
